import {Box, Button, capitalize, Stack} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {DataRequest, DataRequestState, useDataRequest} from "../data";
import {useT} from "../../i18n";
import DataTable from "../data/DataTable";
import {Externessystem} from "../../api";
import {green, red} from "@mui/material/colors";
import {Cancel, CheckCircle} from "@mui/icons-material";
import {
    useDatevAuthUrl,
    useDatevRevoke,
    useGoogleAuthUrl,
    useGoogleRevoke,
    useSucheExternesysteme,
} from "../../client/hooks";
import GoogleButton from "../GoogleButton";
import DatevButton from "../DatevButton";
import {useQueryClient} from "react-query";
import ExterneSystemeUserButton from "./ExterneSystemeUserButton";
import {formatDateTime} from "../../formatterHelpers";
import SearchField from "../SearchField";
import useCustomFilters from "../data/useCustomFilters";

export type ExterneSystemeFilter = {
    search?: string;
    filtered?: string;
};

export type Page<T> = {
    items: T[];
    page: number;
    limit: number;
    totalPages: number;
    totalElements: number;
};

export type Props = Omit<DataRequest<ExterneSystemeFilter>, "filter"> &
    Partial<Pick<DataRequest<ExterneSystemeFilter>, "filter">>;

const tableName: string = "externesysteme"

export default function ExterneSystemeDataTable({...input}: Props) {
    const request = useDataRequest<ExterneSystemeFilter>({
        filter: {},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <ExterneSystemeResults request={request} setFilteredState={setFilteredState}/>
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ExterneSystemeFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ExterneSystemeFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
        }
    });
    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <SearchField
                value={currentCustomFilter.search}
                onChange={(val) => setCustomField("search", val)}
            />
        </Box>
    );
}

function ExterneSystemeResults({
                                   request,
                                   setFilteredState,
                               }: {
    request: DataRequestState<ExterneSystemeFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<ExterneSystemeFilter>;
    setFilteredState: (value: string) => void;
}) {
    const sucheExternesysteme = useSucheExternesysteme();

    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={sucheExternesysteme}
            size={"large"}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Externessystem>> {
    const {t: translation} = useT("externesysteme");
    const googlemutate = useGoogleAuthUrl();
    const datevmutate = useDatevAuthUrl();
    const revokeGoogle = useGoogleRevoke();
    const revokeDatev = useDatevRevoke();
    const queryClient = useQueryClient();

    function generateState(): string {
        let state = "";
        const characters = "0123456789";
        const length = 30;
        for (let i = 0; i < length; i++) {
            state += characters.charAt(Math.floor(Math.random() * characters.length));
        }

        return state;
    }

    const handleGoogleLogin = async (id: string) => {
        let state = generateState();
        localStorage.setItem("STATE", state);
        let result = await googlemutate(state);
        if (result.authUrl) {
            window.location.href = result.authUrl;
            console.log(`Logging in system with id: ${id}`);
        } else {
            console.error("No auth URL received from Google.");
        }
    };

    const handleDatevLogin = async (id: string) => {
        try {
            let state = generateState();
            localStorage.setItem("DATEV_STATE", state);
            let result = await datevmutate(state);
            if (result.authUrl) {
                window.location.href = result.authUrl;
                console.log(`Logging in system with id: ${id}`);
            } else {
                console.error("No auth URL received from Datev.");
            }
        } catch (error) {
            console.error("Error during Datev login:", error);
        }
    };

    const handleLogout = async (id: string) => {
        let result = await revokeGoogle();
        await queryClient.invalidateQueries("externesysteme");
        alert(result.message);
        console.log(`Logging out system with id: ${id}`);
    };

    const handleDatevLogout = async (id: string) => {
        let result = await revokeDatev();
        await queryClient.invalidateQueries("externesysteme");
        alert(result.message);
        console.log(`Logging out system with id: ${id}`);
    };

    return useMemo(
        () => [
            {
                field: "name",
                headerName: capitalize(translation("system")),
                flex: 2,
            },
            {
                field: "status",
                headerName: capitalize(translation("status")),
                flex: 2,
                renderCell: (params) => {
                    const {refreshTokenDueDate, refreshToken, name} = params.row;
                    const isExpired = refreshTokenDueDate
                        ? new Date(refreshTokenDueDate) < new Date()
                        : false;

                    const isConnected = refreshToken && !isExpired;

                    return (
                        <Box display="flex" alignItems="center">
                            {isConnected ? (
                                <>
                                    <CheckCircle sx={{color: green[500], mr: 1}}/>
                                    {capitalize(translation("connected"))}
                                    {name === "DATEV" && <ExterneSystemeUserButton/>}
                                </>
                            ) : (
                                <>
                                    <Cancel sx={{color: red[500], mr: 1}}/>
                                    {capitalize(translation("disconnected"))}
                                </>
                            )}
                        </Box>
                    );
                },
            },
            {
                field: "accessTokenDueDate",
                headerName: capitalize(translation("token-valid-until")),
                flex: 2,
                renderCell: (params) => {
                    const tokenDueDate = params.row.refreshTokenDueDate;
                    if (tokenDueDate) {
                        return formatDateTime(tokenDueDate);
                    } else {
                        return capitalize(translation("no-expiry-date"));
                    }
                },
            },
            {
                field: "btnLogin",
                headerName: capitalize(translation("new-login")),
                flex: 2,
                renderCell: (params) => {
                    const {typ, id} = params.row;

                    return (
                        <>
                            {typ === "google" && (
                                <GoogleButton onClick={() => handleGoogleLogin(id)}>
                                    {capitalize(translation("login-google"))}
                                </GoogleButton>
                            )}
                            {typ === "datev" && (
                                <DatevButton
                                    loading={false}
                                    onClick={() => handleDatevLogin(id)}
                                >
                                    {capitalize(translation("login-datev"))}
                                </DatevButton>
                            )}
                        </>
                    );
                },
            },
            {
                field: "btnLogout",
                headerName: capitalize(translation("log-out")),
                flex: 2,
                renderCell: (params) => {
                    const {id, typ, refreshToken, refreshTokenDueDate} = params.row;
                    const isExpired = refreshTokenDueDate
                        ? new Date(refreshTokenDueDate) < new Date()
                        : false;

                    const isConnected = refreshToken && !isExpired;

                    return (
                        <>
                            {typ === "google" && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!isConnected}
                                    onClick={() => handleLogout(id)}
                                >
                                    {capitalize(translation("log-out"))}
                                </Button>
                            )}
                            {typ === "datev" && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!isConnected}
                                    onClick={() => handleDatevLogout(id)}
                                >
                                    {capitalize(translation("log-out"))}
                                </Button>
                            )}
                        </>
                    );
                },
            },
        ],
        [translation]
    );
}
